/**
 * Analítica de búsquedas del Home: registro de cada búsqueda ejecutada para métricas en admin.
 * Solo se registra cuando el usuario ejecuta la búsqueda (Enter o clic en Buscar), no en cada tecla.
 */
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const COLECCION = 'search_logs';

/** Conectores y palabras a ignorar al extraer tokens útiles. */
const STOPWORDS = new Set([
  'de', 'del', 'la', 'el', 'los', 'las', 'un', 'una', 'y', 'con', 'para', 'por',
  'en', 'al', 'a', 'o', 'e', 'si', 'no', 'que', 'da', 'do', 'es', 'se', 'lo', 'le'
]);

/**
 * Normaliza el texto de búsqueda: minúsculas, sin tildes, trim, colapsar espacios.
 * @param {string} value
 * @returns {string}
 */
export function normalizeSearchQuery(value) {
  if (value == null) return '';
  const s = String(value).trim().toLowerCase().replace(/\s+/g, ' ');
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}

/**
 * Extrae tokens útiles (palabras >= 2 caracteres, sin stopwords).
 * @param {string} query
 * @returns {string[]}
 */
export function tokenizeSearchQueryForAnalytics(query) {
  if (!query || typeof query !== 'string') return [];
  const normalized = normalizeSearchQuery(query);
  const raw = normalized.split(/\s+/).filter(Boolean);
  const tokens = raw
    .filter((t) => t.length >= 2 && !STOPWORDS.has(t))
    .filter((t, i, arr) => arr.indexOf(t) === i);
  return tokens;
}

/**
 * Infiere el tipo principal de búsqueda según listas conocidas.
 * @param {string[]} tokens
 * @param {{ knownBrands?: string[], knownServices?: string[] }} options
 * @returns {'marca'|'servicio'|'mixta'|'libre'}
 */
export function inferSearchType(tokens, options = {}) {
  if (!Array.isArray(tokens) || tokens.length === 0) return 'libre';
  const brands = (options.knownBrands || []).map((b) => normalizeSearchQuery(String(b)));
  const services = (options.knownServices || []).map((s) => normalizeSearchQuery(String(s)));

  let matchBrand = false;
  let matchService = false;
  for (const t of tokens) {
    if (brands.some((b) => b === t || b.includes(t) || t.includes(b))) matchBrand = true;
    if (services.some((s) => s === t || s.includes(t) || t.includes(s))) matchService = true;
  }

  if (matchBrand && matchService) return 'mixta';
  if (matchBrand) return 'marca';
  if (matchService) return 'servicio';
  return 'libre';
}

/** Ventana en ms para considerar duplicado (misma query). */
const DEDUPE_MS = 4000;

/**
 * Indica si se debe registrar la búsqueda (evita vacíos y duplicados inmediatos).
 * @param {string} queryOriginal
 * @param {{ lastQuery?: string, lastTime?: number }} state
 * @returns {boolean}
 */
export function shouldLogSearch(queryOriginal, state = {}) {
  const q = (queryOriginal || '').trim();
  if (q.length < 2) return false;
  if (/^\s*$/.test(q)) return false;

  const { lastQuery, lastTime } = state;
  if (lastQuery != null && lastTime != null) {
    const norm = normalizeSearchQuery(q);
    const lastNorm = normalizeSearchQuery(lastQuery);
    if (norm === lastNorm && Date.now() - lastTime < DEDUPE_MS) return false;
  }
  return true;
}

const ORIGEN_HOME_SEARCH = 'home-search';

/**
 * Arma el payload del log de búsqueda.
 * @param {Object} params
 * @param {string} params.queryOriginal
 * @param {number} params.resultadosEncontrados
 * @param {string|null} params.userId
 * @param {string[]} [params.tokens]
 * @param {string} [params.tipoBusquedaInferida]
 * @param {string|null} [params.regionUsuario]
 * @param {string|null} [params.comunaUsuario]
 * @param {string|null} [params.anonymousSessionId]
 */
export function buildSearchLogPayload({
  queryOriginal,
  resultadosEncontrados,
  userId = null,
  tokens = [],
  tipoBusquedaInferida = 'libre',
  regionUsuario = null,
  comunaUsuario = null,
  anonymousSessionId = null,
}) {
  const queryNorm = normalizeSearchQuery(queryOriginal);
  return {
    queryOriginal: (queryOriginal || '').trim(),
    queryNormalizada: queryNorm,
    tokens: Array.isArray(tokens) ? tokens : [],
    tipoBusquedaInferida: tipoBusquedaInferida || 'libre',
    resultadosEncontrados: typeof resultadosEncontrados === 'number' ? resultadosEncontrados : 0,
    userId: userId || null,
    origen: ORIGEN_HOME_SEARCH,
    regionUsuario: regionUsuario || null,
    comunaUsuario: comunaUsuario || null,
    anonymousSessionId: anonymousSessionId || null,
    createdAt: serverTimestamp(),
    clickedResult: null, // reservado para futuro
  };
}

const SESSION_KEY = 'kawescar_search_anon_id';

/**
 * Obtiene o crea un ID de sesión anónima (sessionStorage) para agrupar búsquedas del mismo visitante.
 * @returns {string|null}
 */
export function getOrCreateAnonymousSessionId() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * Guarda el log de búsqueda en Firestore. No lanza: errores se manejan en silencio para no afectar UX.
 * @param {Object} payload - Objeto devuelto por buildSearchLogPayload (sin createdAt si se añade aquí)
 */
export async function saveSearchLog(payload) {
  if (!db) return;
  if (!payload || typeof payload !== 'object') return;
  const docData = {
    ...payload,
    createdAt: serverTimestamp(),
  };
  try {
    await addDoc(collection(db, COLECCION), docData);
  } catch (err) {
    console.warn('⚠️ No se pudo guardar log de búsqueda:', err?.message);
  }
}
